// src/features/durations/components/DurationsPage.tsx
//
// «Tiempos» (mockup 07) — route /durations, Dirección. Medians/p75, the
// standard|fastKiosk bucket switch, sections, byHour/byWeekday with
// «Periodo anterior», lead-time buckets.
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { StackedBarsChart } from '@/components/common/analytics/StackedBarsChart';
import { SampledFigure } from '@/components/common/analytics/SampledFigure';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { useViewSwitch } from '@/providers/preferencesProvider';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { getSectionLabel } from '@/design/registry/sectionRegistry';
import { formatCount, formatPct, formatSeconds } from '@/lib/analyticsFormat';
import type { DurationChannelStat, DurationSection, DurationsResponse } from '@/types/analytics';
import type { LiveChannel } from '@/types/reservationState';

const CHANNEL_ORDER: LiveChannel[] = ['Online', 'Kiosk', 'MobileKiosk', 'AirLobby'];
const theme = { primary: '#1499AD', previous: '#CDC7BB' };

export const DurationsPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const exportCsv = useCsvExport({ page: 'durations' });
  const canExport = hasResourcePermission('durations', 'export');
  const [buckets, setBuckets] = useViewSwitch('durations', 'buckets', 'standard');

  const [period, setPeriod] = useAnalyticsPeriod();
  const { data, loading, error, refresh } = useAnalyticsFetch<DurationsResponse>(
    () => (dataProvider as any).getAnalyticsDurations({ ...scope.scopeParams, from: period.from, to: period.to, buckets }),
    [dataProvider, scope.scopeParams, period.from, period.to, buckets]
  );

  const primaryChannel: DurationChannelStat | undefined = data?.channels.find((c) => c.channel === 'Kiosk' && c.count > 0) ?? data?.channels[0];

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data.channels,
      [
        { labelKey: 'resources.durations.table.columns.channel', value: (r: DurationChannelStat) => translate(getChannelShortLabelKey(r.channel)) },
        { labelKey: 'resources.durations.table.columns.count', value: (r: DurationChannelStat) => r.count },
        { labelKey: 'resources.durations.table.columns.medianSeconds', value: (r: DurationChannelStat) => r.medianSeconds ?? '' },
      ],
      { from: period.from, to: period.to }
    );
  };

  const minimum = data?.minimumSample;
  const channelColumns: MetricsColumn<DurationChannelStat>[] = [
    { key: 'channel', label: translate('resources.durations.table.columns.channel'), render: (row) => translate(getChannelShortLabelKey(row.channel)) },
    { key: 'count', label: translate('resources.durations.table.columns.count'), align: 'right', render: (row) => formatCount(row.count) },
    { key: 'medianSeconds', label: translate('resources.durations.table.columns.medianSeconds'), align: 'right', render: (row) => <SampledFigure value={row.medianSeconds} sample={row.count} minimum={minimum} format={formatSeconds} /> },
    { key: 'p75Seconds', label: translate('resources.durations.table.columns.p75Seconds'), align: 'right', render: (row) => <SampledFigure value={row.p75Seconds} sample={row.count} minimum={minimum} format={formatSeconds} /> },
    { key: 'under2MinPct', label: translate('resources.durations.table.columns.under2MinPct'), align: 'right', render: (row) => <SampledFigure value={row.under2MinPct} sample={row.count} minimum={minimum} format={formatPct} /> },
    { key: 'under5MinPct', label: translate('resources.durations.table.columns.under5MinPct'), align: 'right', render: (row) => <SampledFigure value={row.under5MinPct} sample={row.count} minimum={minimum} format={formatPct} /> },
  ];
  const channelRows = CHANNEL_ORDER.map((ch) => data?.channels.find((c) => c.channel === ch)).filter((row): row is DurationChannelStat => Boolean(row));

  // «Por franjas de duración»: one column per range of the user's bucket preference
  // (the API returns the ranges in order, the same set for every channel).
  const rangeLabels = channelRows[0]?.buckets.map((b) => b.label) ?? [];
  const rangeColumns: MetricsColumn<DurationChannelStat>[] = [
    { key: 'channel', label: translate('resources.durations.table.columns.channel'), render: (row) => translate(getChannelShortLabelKey(row.channel)) },
    ...rangeLabels.map((label) => ({
      key: `range-${label}`,
      label: translate(`resources.durations.ranges.labels.${label}`, { _: label }),
      align: 'right' as const,
      render: (row: DurationChannelStat) => formatCount(row.buckets.find((b) => b.label === label)?.count ?? 0),
    })),
  ];

  const sectionColumns: MetricsColumn<DurationSection>[] = [
    { key: 'section', label: translate('resources.durations.sections.columns.section'), render: (s) => getSectionLabel(s.section, translate) },
    { key: 'count', label: translate('resources.durations.sections.columns.count'), align: 'right', render: (s) => formatCount(s.count) },
    { key: 'medianSeconds', label: translate('resources.durations.sections.columns.medianSeconds'), align: 'right', render: (s) => <SampledFigure value={s.medianSeconds} sample={s.count} minimum={minimum} format={formatSeconds} /> },
    { key: 'p75Seconds', label: translate('resources.durations.sections.columns.p75Seconds'), align: 'right', render: (s) => <SampledFigure value={s.p75Seconds} sample={s.count} minimum={minimum} format={formatSeconds} /> },
  ];

  return (
    <Box>
      <SectionHeader manualScreen="durations"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
            <ToggleButtonGroup size="small" exclusive value={buckets} onChange={(_, v) => v && setBuckets(v)}>
              <ToggleButton value="standard" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate('resources.durations.bucketsSwitch.standard')}
              </ToggleButton>
              <ToggleButton value="fastKiosk" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
                {translate('resources.durations.bucketsSwitch.fastKiosk')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
        subtitle={translate('resources.durations.subtitle')}
        helpConfig={{
          titleKey: 'resources.durations.help.title',
          descriptionKey: 'resources.durations.help.description',
          featureKeys: ['resources.durations.help.features.buckets', 'resources.durations.help.features.leadTime', 'resources.durations.help.features.sample'],
          tipKeys: ['resources.durations.help.tips.medians'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      <WidgetCard title={translate('resources.durations.table.title')} description={translate('resources.durations.table.description')} sx={{ mb: 2.5 }}>
        <ResponsiveMetricsTable<DurationChannelStat> columns={channelColumns} rows={channelRows} getRowKey={(row) => row.channel} loading={loading} />
      </WidgetCard>

      <WidgetCard title={translate('resources.durations.ranges.title')} description={translate('resources.durations.ranges.description')} sx={{ mb: 2.5 }}>
        <ResponsiveMetricsTable<DurationChannelStat> columns={rangeColumns} rows={channelRows} getRowKey={(row) => row.channel} loading={loading} />
      </WidgetCard>

      {primaryChannel && primaryChannel.sections.length > 0 && (
        <WidgetCard
          title={`${translate('resources.durations.sections.title')} · ${translate(getChannelShortLabelKey(primaryChannel.channel))}`}
          sx={{ mb: 2.5 }}
        >
          <ResponsiveMetricsTable<DurationSection> columns={sectionColumns} rows={primaryChannel.sections} getRowKey={(s) => s.section} loading={loading} />
        </WidgetCard>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
        <WidgetCard title={translate('resources.durations.byHour.title')} description={translate('resources.durations.byHour.description')}>
          <StackedBarsChart
            data={data?.byHour ?? []}
            xKey="hour"
            stacked={false}
            height={200}
            series={[
              { key: 'count', labelKey: 'resources.durations.byHour.series.count', color: theme.primary },
              { key: 'previous', labelKey: 'resources.durations.byHour.series.previous', color: theme.previous },
            ]}
          />
        </WidgetCard>
        <WidgetCard title={translate('resources.durations.byWeekday.title')} description={translate('resources.durations.byWeekday.description')}>
          <StackedBarsChart
            data={(data?.byWeekday ?? []).map((d) => ({ ...d, weekdayLabel: translate(`resources.durations.byWeekday.days.${d.weekday - 1}` as any) }))}
            xKey="weekdayLabel"
            stacked={false}
            height={200}
            series={[
              { key: 'count', labelKey: 'resources.durations.byWeekday.series.count', color: theme.primary },
              { key: 'previous', labelKey: 'resources.durations.byWeekday.series.previous', color: theme.previous },
            ]}
          />
        </WidgetCard>
      </Box>

      <WidgetCard title={translate('resources.durations.leadTime.title')} description={translate('resources.durations.leadTime.description')}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {(data?.leadTime.buckets ?? []).map((b) => (
                <TableRow key={b.label}>
                  <TableCell>{translate(`resources.durations.leadTime.buckets.${b.label}`)}</TableCell>
                  <TableCell align="right">{formatCount(b.count)}</TableCell>
                  <TableCell align="right">
                    <SampledFigure value={b.pct} sample={data?.leadTime.total ?? 0} minimum={minimum} format={formatPct} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data?.leadTime.medianDays != null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {translate('resources.durations.leadTime.medianDays', { days: formatCount(data.leadTime.medianDays), smart_count: data.leadTime.medianDays })}
          </Typography>
        )}
        {data && data.leadTime.excludedNegative > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {translate('resources.durations.leadTime.excludedNegative', {
              count: formatCount(data.leadTime.excludedNegative),
              smart_count: data.leadTime.excludedNegative,
            })}
          </Typography>
        )}
      </WidgetCard>
    </Box>
  );
};

export default DurationsPage;
